import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import IdpHelpSidebar from '../components/IdpHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import { idpService } from '../services/idpService';
import { apiService } from '../services/api';
import { fiscalYearService } from '../services/fiscalYearService';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateStandard, formatCalendarDateIso, utcMs } from '../utils/dateFormat';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createShouldDisableDate } from '../utils/dateValidation';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import type { AxiosError } from 'axios';
import type {
  IndividualDevelopmentPlan,
  DevelopmentGoal,
  IdpQuarterlyReview,
  UpdateIdpDto,
  UpdateDevelopmentGoalDto,
  IdpAuditEvent,
  CreateIdpDto,
} from '../types/idp';
import { IdpStatus, IdpAuditEventType } from '../types/idp';
import { formatIdpStatus, getIdpStatusColor } from '../utils/idpStatus';

/** Existing quarterly review or a slot for a quarter not yet started (IDP year = fiscal year). */
type QuarterlyReviewSlot = IdpQuarterlyReview | { reviewNumber: number; reviewCycleId: string; reviewCycleName: string };

function isExistingQuarterlyReview(slot: QuarterlyReviewSlot): slot is IdpQuarterlyReview {
  return 'id' in slot;
}

/** Controls and navigation — hidden when printing. */
const printHiddenSx = { '@media print': { display: 'none !important' } };

/** True if current goals list differs from saved (idp) by content (ignores temp ids). */
function goalsContentDiffers(current: DevelopmentGoal[], saved: DevelopmentGoal[]): boolean {
  if (current.length !== saved.length) return true;
  const norm = (g: DevelopmentGoal) => ({
    skillOrCompetency: g.skillOrCompetency,
    resourcesAndActivities: g.resourcesAndActivities,
    targetDeadline: g.targetDeadline,
    sequence: g.sequence,
  });
  const a = [...current].sort((x, y) => x.sequence - y.sequence).map(norm);
  const b = [...saved].sort((x, y) => x.sequence - y.sequence).map(norm);
  return a.some((c, i) =>
    c.skillOrCompetency !== b[i].skillOrCompetency ||
    c.resourcesAndActivities !== b[i].resourcesAndActivities ||
    c.targetDeadline !== b[i].targetDeadline ||
    c.sequence !== b[i].sequence
  );
}

/** Career aspirations field with local state and debounced sync to parent to avoid re-rendering the whole page on every keystroke. */
function CareerAspirationsField({
  value,
  onChange,
  latestValueRef,
  ...textFieldProps
}: {
  value: string;
  onChange: (value: string) => void;
  /** Optional ref updated on every keystroke so parent can read latest value when saving without waiting for debounce. */
  latestValueRef?: React.MutableRefObject<string>;
} & React.ComponentProps<typeof TextField>) {
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef<((value: string) => void) | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
    return () => {
      onChangeRef.current = null;
    };
  }, [onChange]);

  // Sync from parent when value changes (e.g. IDP loaded)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const debouncedOnChange = useCallback((newValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onChangeRef.current?.(newValue);
    }, 300);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (latestValueRef) latestValueRef.current = newValue;
    debouncedOnChange(newValue);
  };

  return (
    <TextField
      fullWidth
      multiline
      rows={6}
      value={localValue}
      onChange={handleChange}
      placeholder="Describe your career aspirations and goals..."
      required
      sx={{ mt: 2 }}
      {...textFieldProps}
    />
  );
}

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSave, couldNotSubmit, couldNotUpdate, couldNotCreate, couldNotApprove, documentCopy } from '../utils/userFacingCopy';
import { SortableTableHeader } from '../utils/sortableTableHeader';

export default function IndividualDevelopmentPlanPage() {
  const { user: currentUser, displayTimeZoneId } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);
  const navigate = useNavigate();
  const { employeeId, year: yearFromPath } = useParams<{ employeeId?: string; year?: string }>();
  const [searchParams] = useSearchParams();
  const yearParam = searchParams.get('year') || yearFromPath; // Check both query param and path param
  const pendingApprovalsParam = searchParams.get('pending-approvals');
  const teamParam = searchParams.get('team');
  const urlDepartmentId = searchParams.get('departmentId');
  const urlDepartmentName = searchParams.get('departmentName');
  const urlStatusParam = searchParams.get('status');
  /** Set to true to show the Quarterly Review of Individual's Progress section (currently disabled to reduce confusion). */
  const SHOW_QUARTERLY_REVIEW_SECTION = false;
  const [idps, setIdps] = useState<IndividualDevelopmentPlan[]>([]);
  const [idp, setIdp] = useState<IndividualDevelopmentPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [careerAspirations, setCareerAspirations] = useState('');
  const careerAspirationsRef = useRef('');
  const [goals, setGoals] = useState<DevelopmentGoal[]>([]);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<DevelopmentGoal | null>(null);
  const [editingGoal, setEditingGoal] = useState<DevelopmentGoal | null>(null);
  const [goalForm, setGoalForm] = useState({
    skillOrCompetency: '',
    resourcesAndActivities: '',
    targetDeadline: '',
    sequence: 0,
  });
  const [quarterlyReviewDialogOpen, setQuarterlyReviewDialogOpen] = useState(false);
  const [selectedQuarterlyReview, setSelectedQuarterlyReview] = useState<QuarterlyReviewSlot | null>(null);
  const [quarterlyReviewForm, setQuarterlyReviewForm] = useState({
    managerComments: '',
    managerReviewDate: '',
    employeeComments: '',
    employeeReviewDate: '',
  });
  const [auditEvents, setAuditEvents] = useState<IdpAuditEvent[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createYear, setCreateYear] = useState<number>(
    yearParam ? parseInt(yearParam, 10) : new Date().getFullYear()
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'year' | 'status' | 'createdAt'>('year');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [useCalendarYear, setUseCalendarYear] = useState<boolean>(false);
  /** Review cycles for the IDP's fiscal year, keyed by fiscal quarter (1-4). Used to open "Not started" quarterly review. */
  const [cyclesByQuarter, setCyclesByQuarter] = useState<Record<number, { id: string; name: string; startDate: string }>>({});
  const hasUnsavedChanges = useMemo(() => {
    if (!idp) return false;
    const aspirationsDirty = (careerAspirations || '').trim() !== (idp.careerAspirations || '').trim();
    const goalsDirty = goalsContentDiffers(goals, idp.developmentGoals);
    return aspirationsDirty || goalsDirty;
  }, [idp, careerAspirations, goals]);
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'individual-development-plan',
    pageTitle: 'Individual Development Plan',
    viewMode: employeeId ? 'employee' : 'my',
    visibleSections: ['career-aspirations', 'development-goals', 'supporting-documents', 'audit-trail'],
    suggestedPrompts: [
      'How do I update my IDP on this page?',
      'What do the IDP statuses mean?',
      'Who can edit and acknowledge this plan?',
    ],
  });
  // IDP supporting documents (uploadable at any time - working agreement)
  const [idpDocuments, setIdpDocuments] = useState<{ id: string; fileName: string; contentType: string; uploadedByName: string; uploadedAt: string; fileSize: number; description?: string }[]>([]);
  const [loadingIdpDocs, setLoadingIdpDocs] = useState(false);
  const [idpUploadDialogOpen, setIdpUploadDialogOpen] = useState(false);
  const [uploadingIdpDoc, setUploadingIdpDoc] = useState(false);
  const [previewNotAvailableFileName, setPreviewNotAvailableFileName] = useState<string | null>(null);
  const [editIdpDocDialogOpen, setEditIdpDocDialogOpen] = useState(false);
  const [editingIdpDoc, setEditingIdpDoc] = useState<{ id: string; fileName: string; description: string } | null>(null);
  const [deleteConfirmIdpDoc, setDeleteConfirmIdpDoc] = useState<{ documentId: string; fileName: string } | null>(null);
  const [deletingIdpDocId, setDeletingIdpDocId] = useState<string | null>(null);

  // Load system settings to determine if using calendar or fiscal year
  // Note: This endpoint requires admin access, so non-admins will get 403 (expected)
  useEffect(() => {
    const loadSettings = async () => {
      // Check if user is admin before making the call
      const userRoles = currentUser?.roles || [];
      const isAdmin = userRoles.includes('Administrator');
      
      if (!isAdmin) {
        // Non-admins default to fiscal year (the system default)
        setUseCalendarYear(false);
        return;
      }

      try {
        const settingsResponse = await apiService.get<{ useCalendarYear: boolean }>('/System/review-cycle-settings');
        setUseCalendarYear(settingsResponse.data.useCalendarYear);
      } catch (err: unknown) {
        // Silently handle 403 (expected for non-admins) and other errors
        // Default to fiscal year if settings can't be loaded
        const axiosError = err as AxiosError<{ message?: string }>;
        if (axiosError.response?.status !== 403) {
          // Only log non-403 errors (403 is expected for non-admins)
          console.warn('Failed to load system settings:', err);
        }
        setUseCalendarYear(false);
      }
    };

    if (currentUser) {
      loadSettings();
    } else {
      // Default to fiscal year if user not loaded yet
      setUseCalendarYear(false);
    }
  }, [currentUser]);

  // When viewing another employee's IDP (employeeId in path) but no year is specified, redirect to current year so loadIdp runs
  useEffect(() => {
    if (employeeId && !yearParam) {
      const currentYear = new Date().getFullYear();
      navigate(`/individual-development-plan/${employeeId}/${currentYear}`, { replace: true });
    }
  }, [employeeId, yearParam, navigate]);

  // Load list of IDPs when no year is specified
  useEffect(() => {
    const loadIdps = async () => {
      if (!currentUser || yearParam || employeeId) return; // Only load list when viewing own IDPs without year param

      try {
        setLoading(true);
        setError('');
        
        // If pending-approvals param is present, load pending approvals for managers
        if (pendingApprovalsParam === 'true') {
          const pendingIdps = await idpService.getPendingApprovals();
          setIdps(pendingIdps);
        } else if (teamParam === 'true') {
          // If team param is present, load all team IDPs for managers
          const teamIdps = await idpService.getTeamIdps();
          setIdps(teamIdps);
        } else {
          // Otherwise, load user's own IDPs
          const allIdps = await idpService.getMyIdps();
          setIdps(allIdps);
        }
      } catch (err: unknown) {
        console.error('Failed to load IDPs:', err);
        if (err && typeof err === 'object' && 'response' in err) {
                    setError(getApiErrorMessage(err, couldNotLoad('development plans')));
        } else {
          setError(couldNotLoad('development plans'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadIdps();
  }, [currentUser, yearParam, employeeId, pendingApprovalsParam, teamParam]);

  // Sync status filter from URL when arriving from team/pending-approvals dashboard
  useEffect(() => {
    if ((teamParam === 'true' || pendingApprovalsParam === 'true') && urlStatusParam) {
      const map: Record<string, string> = {
        outstanding: 'outstanding',
        draft: IdpStatus.Draft.toString(),
        withmanager: IdpStatus.WithManager.toString(),
        acknowledged: IdpStatus.Acknowledged.toString(),
        completed: IdpStatus.Completed.toString(),
        '4': IdpStatus.Completed.toString(), // backwards compatibility for status=4 in URL
      };
      setStatusFilter(map[urlStatusParam.toLowerCase()] ?? 'all');
      setPage(0);
    }
  }, [teamParam, pendingApprovalsParam, urlStatusParam]);

  // Load specific IDP when year is specified
  useEffect(() => {
    const loadIdp = async () => {
      if (!currentUser || !yearParam) return;

      try {
        setLoading(true);
        setError('');

        const targetYear = parseInt(yearParam, 10);
        const targetEmployeeId = employeeId || currentUser.id;
        
        // Check if edit mode is requested via query parameter
        const editParam = searchParams.get('edit') === 'true';

        let loadedIdp: IndividualDevelopmentPlan | null;
        if (employeeId && employeeId !== currentUser.id) {
          // Manager viewing employee's IDP
          loadedIdp = await idpService.getEmployeeIdp(targetEmployeeId, targetYear);
        } else {
          // Employee viewing their own IDP
          loadedIdp = await idpService.getMyIdp(targetYear);
        }

        if (!loadedIdp) {
          setError('IDP not found for this year.');
          setLoading(false);
          return;
        }

        setIdp(loadedIdp);
        const aspirations = loadedIdp.careerAspirations || '';
        setCareerAspirations(aspirations);
        careerAspirationsRef.current = aspirations;
        setGoals([...loadedIdp.developmentGoals].sort((a, b) => a.sequence - b.sequence));

        // Auto-enter edit mode if:
        // 1. edit=true query parameter is present, OR
        // 2. IDP is newly created (Draft status with no goals and no career aspirations)
        const status = loadedIdp.status;
        const isDraft = status === IdpStatus.Draft || status === 1 || status === 'Draft';
        const isNewlyCreated = isDraft && 
            (!loadedIdp.careerAspirations || loadedIdp.careerAspirations.trim() === '') &&
            loadedIdp.developmentGoals.length === 0 &&
            loadedIdp.employeeId === currentUser.id;
        
        if (editParam || isNewlyCreated) {
          // Check if user can actually edit this IDP
          const isSubmitted = status === IdpStatus.WithManager || status === 2 || status === 'Submitted';
          const isActive = status === IdpStatus.Acknowledged || status === 3 || status === 'Active' || status === 'Acknowledged';
          const canEditIdp = loadedIdp.employeeId === currentUser.id && (isDraft || isSubmitted || isActive);
          
          if (canEditIdp) {
            setEditing(true);
          }
        }

        // Load audit trail
        try {
          setLoadingAudit(true);
          const events = await idpService.getAuditTrail(loadedIdp.id);
          setAuditEvents(events);
        } catch (err: unknown) {
          console.error('Failed to load audit trail:', err);
        } finally {
          setLoadingAudit(false);
        }
      } catch (err: unknown) {
        console.error('Failed to load IDP:', err);
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as AxiosError<{ message?: string }>;
          if (axiosError.response?.status === 404) {
            setError('IDP not found for this year.');
          } else if (axiosError.response?.status === 403) {
            setError('You do not have permission to view this IDP');
          } else {
            setError(getApiErrorMessage(err, couldNotLoad('the development plan')));
          }
        } else {
          setError(couldNotLoad('the development plan'));
        }
      } finally {
        setLoading(false);
      }
    };

    loadIdp();
  }, [currentUser, employeeId, yearParam, searchParams]);

  // When viewing a single IDP, load review cycles for the IDP's year and map to quarters (1-4).
  // Only include cycles where IsActive is true so multiple can be open at once (e.g. finalisation lags).
  // Use the same alignment as the backend (UseCalendarYear) so IDP and cycles are never aligned differently.
  useEffect(() => {
    if (!idp?.year) {
      setCyclesByQuarter({});
      return;
    }
    const idpYear = idp.year;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.get<{ id: string; name: string; startDate: string; isActive: boolean }[]>('/review-cycles');
        const cycles = response.data || [];
        const byQuarter: Record<number, { id: string; name: string; startDate: string }> = {};
        for (const cycle of cycles) {
          if (cancelled) return;
          if (!cycle.isActive) continue;
          const { year, quarter } = await fiscalYearService.evaluateReviewYearDate(cycle.startDate);
          if (year === idpYear && quarter >= 1 && quarter <= 4) {
            byQuarter[quarter] = { id: cycle.id, name: cycle.name, startDate: cycle.startDate };
          }
        }
        if (!cancelled) setCyclesByQuarter(byQuarter);
      } catch {
        if (!cancelled) setCyclesByQuarter({});
      }
    })();
    return () => { cancelled = true; };
  }, [idp?.year]);

  // Load IDP supporting documents when viewing a single IDP
  useEffect(() => {
    if (!idp?.id) {
      setIdpDocuments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingIdpDocs(true);
      try {
        const res = await apiService.get<{ id: string; fileName: string; contentType: string; uploadedByName: string; uploadedAt: string; fileSize: number; description?: string }[]>(
          `/individual-development-plans/${idp.id}/supporting-documents`
        );
        if (!cancelled) setIdpDocuments(res.data || []);
      } catch {
        if (!cancelled) setIdpDocuments([]);
      } finally {
        if (!cancelled) setLoadingIdpDocs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idp?.id]);

  // Filter and sort IDPs
  const filteredAndSortedIdps = useMemo(() => {
    let filtered = idps;

    // Apply department filter from URL (click-through from org dashboard)
    if (urlDepartmentId || urlDepartmentName) {
      const deptId = urlDepartmentId?.trim();
      const deptName = urlDepartmentName?.trim();
      if (deptId || deptName) {
        filtered = filtered.filter((idp) => {
          const ids = idp.departmentIds ?? [];
          const names = idp.departmentNames ?? [];
          if (deptId && ids.includes(deptId)) return true;
          if (deptName && names.some((n) => n?.toLowerCase() === deptName.toLowerCase())) return true;
          return false;
        });
      }
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'outstanding') {
        filtered = filtered.filter((idp) => {
          const s = idp.status;
          const isDraft = s === IdpStatus.Draft || s === 1 || s === 'Draft';
          const isWithManager = s === IdpStatus.WithManager || s === 2 || s === 'WithManager';
          return isDraft || isWithManager;
        });
      } else {
        const statusValue = parseInt(statusFilter, 10);
        filtered = filtered.filter((idp) => {
          const idpStatus = idp.status;
          if (typeof idpStatus === 'string') {
            const stringToNumber: Record<string, number> = {
              'Draft': 1,
              'WithManager': 2,
              'Acknowledged': 3,
              'Completed': 4,
            };
            return stringToNumber[idpStatus] === statusValue;
          }
          return idpStatus === statusValue;
        });
      }
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (idp) =>
          idp.year.toString().includes(query) ||
          formatIdpStatus(idp.status).toLowerCase().includes(query) ||
          (idp.careerAspirations && idp.careerAspirations.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'year':
          aValue = a.year;
          bValue = b.year;
          break;
        case 'status':
          aValue = formatIdpStatus(a.status);
          bValue = formatIdpStatus(b.status);
          break;
        case 'createdAt':
          aValue = utcMs(a.createdAt);
          bValue = utcMs(b.createdAt);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [idps, searchQuery, statusFilter, sortBy, sortDirection, urlDepartmentId, urlDepartmentName]);

  // Paginate filtered results
  const paginatedIdps = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedIdps.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedIdps, page, rowsPerPage]);

  const handleSort = (column: 'year' | 'status' | 'createdAt') => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getIdpEventTypeLabel = (eventType: IdpAuditEventType | string | number): string => {
    // Handle string enum names from API
    if (typeof eventType === 'string') {
      const stringLabels: Record<string, string> = {
        'IdpCreated': 'IDP Created',
        'IdpSubmitted': 'IDP Submitted',
        'IdpAcknowledged': 'IDP Acknowledged',
        'QuarterlyReviewSubmitted': 'Quarterly Review Submitted',
        'IdpRevertedToDraftForReview': 'IDP Reverted to Draft (Review)',
        'IdpUpdated': 'IDP Updated',
        'SupportingDocumentUploaded': 'Supporting Document Uploaded',
        'SupportingDocumentDeleted': 'Supporting Document Deleted',
        'SupportingDocumentUpdated': 'Supporting Document Updated',
      };
      if (stringLabels[eventType]) {
        return stringLabels[eventType];
      }
    }

    // Handle numeric enum values
    const numericValue = typeof eventType === 'number' ? eventType : (typeof eventType === 'string' ? parseInt(eventType, 10) : eventType);
    const labels: Record<IdpAuditEventType, string> = {
      [IdpAuditEventType.IdpCreated]: 'IDP Created',
      [IdpAuditEventType.IdpSubmitted]: 'IDP Submitted',
      [IdpAuditEventType.IdpAcknowledged]: 'IDP Acknowledged',
      [IdpAuditEventType.QuarterlyReviewSubmitted]: 'Quarterly Review Submitted',
      [IdpAuditEventType.IdpRevertedToDraftForReview]: 'IDP Reverted to Draft (Review)',
      [IdpAuditEventType.IdpUpdated]: 'IDP Updated',
      [IdpAuditEventType.SupportingDocumentUploaded]: 'Supporting Document Uploaded',
      [IdpAuditEventType.SupportingDocumentDeleted]: 'Supporting Document Deleted',
      [IdpAuditEventType.SupportingDocumentUpdated]: 'Supporting Document Updated',
    };
    
    if (typeof numericValue === 'number' && labels[numericValue as IdpAuditEventType]) {
      return labels[numericValue as IdpAuditEventType];
    }
    
    // Fallback: try to convert string enum name to readable format
    if (typeof eventType === 'string') {
      return eventType
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    }
    
    return 'Unknown Event';
  };

  const canEdit = () => {
    if (!idp || !currentUser) return false;
    // Employee can edit when Draft, Submitted, or Active (not Completed)
    if (idp.employeeId === currentUser.id) {
      // Handle both string and numeric status values from API
      const status = idp.status;
      const isDraft = status === IdpStatus.Draft || status === 1 || status === 'Draft';
      const isSubmitted = status === IdpStatus.WithManager || status === 2 || status === 'Submitted';
      const isActive = status === IdpStatus.Acknowledged || status === 3 || status === 'Active' || status === 'Acknowledged';
      return isDraft || isSubmitted || isActive;
    }
    return false;
  };

  const canSubmit = () => {
    if (!idp || !currentUser) return false;
    const status = idp.status;
    return idp.employeeId === currentUser.id && (status === IdpStatus.Draft || status === 1 || status === 'Draft');
  };

  const canApprove = () => {
    if (!idp || !currentUser) return false;
    const isManager = idp.managerId === currentUser.id;
    if (!isManager) return false;
    // API serializes enums as strings (JsonStringEnumConverter)
    const status = idp.status;
    const isWithManager = status === IdpStatus.WithManager || status === 2 || status === 'WithManager';
    return isWithManager;
  };

  const handleSave = async () => {
    if (!idp) return;

    try {
      setSaving(true);
      setError('');

      const aspirationsToSave = careerAspirationsRef.current ?? careerAspirations;
      if (!aspirationsToSave || aspirationsToSave.trim() === '') {
        setError('Career aspirations are required');
        return;
      }

      // Validate all goals have target deadlines (only when there are goals)
      const goalsWithoutDeadlines = goals.filter(g => !g.targetDeadline);
      if (goalsWithoutDeadlines.length > 0) {
        setError('All development goals must have a target deadline');
        return;
      }

      // Helper function to check if a string is a valid GUID
      const isValidGuid = (id: string | undefined): boolean => {
        if (!id || id.trim() === '' || id.startsWith('temp-')) return false;
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      };

      // Build the payload directly from goals, only including valid GUIDs
      const updateDto: UpdateIdpDto = {
        careerAspirations: aspirationsToSave.trim(),
        developmentGoals: goals.map((g, index) => {
          const goal: Partial<UpdateDevelopmentGoalDto> = {
            skillOrCompetency: g.skillOrCompetency || '',
            resourcesAndActivities: g.resourcesAndActivities || '',
            targetDeadline: g.targetDeadline, // Required - already validated above
            sequence: index + 1,
          };
          
          // Only include id if it's a valid GUID (not a temporary ID like "temp-...")
          if (isValidGuid(g.id)) {
            goal.id = g.id;
          }
          // If id is invalid or temp, don't include it at all (not even as undefined)
          
          return goal as UpdateDevelopmentGoalDto;
        }),
      };

      const updated = await idpService.updateIdp(idp.id, updateDto);
      setIdp(updated);
      const savedAspirations = updated.careerAspirations || '';
      setCareerAspirations(savedAspirations);
      careerAspirationsRef.current = savedAspirations;
      setGoals([...updated.developmentGoals].sort((a, b) => a.sequence - b.sequence));
      setEditing(false);

      // Refresh audit trail to show the IdpUpdated event
      try {
        setLoadingAudit(true);
        const events = await idpService.getAuditTrail(updated.id);
        setAuditEvents(events);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail:', err);
      } finally {
        setLoadingAudit(false);
      }
    } catch (err: unknown) {
      console.error('Failed to save IDP:', err);
      setError(getApiErrorMessage(err, couldNotSave('the development plan')));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!idp) return;

    try {
      setSaving(true);
      setError('');

      const aspirationsToSubmit = careerAspirationsRef.current ?? careerAspirations;
      if (!aspirationsToSubmit || aspirationsToSubmit.trim() === '') {
        setError('Career aspirations are required before submission');
        return;
      }

      if (goals.length === 0) {
        setError('At least one development goal is required before submission');
        return;
      }

      const goalsWithoutDeadlines = goals.filter(g => !g.targetDeadline);
      if (goalsWithoutDeadlines.length > 0) {
        setError('All development goals must have a target deadline before submission');
        return;
      }

      const updated = await idpService.submitIdp(idp.id);
      setIdp(updated);

      // Refresh audit trail to show the submit event
      try {
        setLoadingAudit(true);
        const events = await idpService.getAuditTrail(updated.id);
        setAuditEvents(events);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail:', err);
      } finally {
        setLoadingAudit(false);
      }
    } catch (err: unknown) {
      console.error('Failed to submit IDP:', err);
      if (err && typeof err === 'object' && 'response' in err) {
                setError(getApiErrorMessage(err, couldNotSubmit('the development plan')));
      } else {
        setError(couldNotSubmit('the development plan'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!idp) return;

    try {
      setSaving(true);
      setError('');

      const updated = await idpService.approveIdp(idp.id);
      setIdp(updated);
      
      // Refresh audit trail to show the approval event
      try {
        setLoadingAudit(true);
        const events = await idpService.getAuditTrail(updated.id);
        setAuditEvents(events);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail:', err);
      } finally {
        setLoadingAudit(false);
      }
      
      // If we came from pending approvals, navigate back to the list (preserve department filter)
      if (pendingApprovalsParam === 'true') {
        const params = new URLSearchParams();
        params.set('pending-approvals', 'true');
        if (urlDepartmentId) params.set('departmentId', urlDepartmentId);
        if (urlDepartmentName) params.set('departmentName', urlDepartmentName);
        navigate(`/individual-development-plan?${params.toString()}`);
      }
    } catch (err: unknown) {
      console.error('Failed to approve IDP:', err);
      if (err && typeof err === 'object' && 'response' in err) {
                setError(getApiErrorMessage(err, couldNotApprove('the development plan')));
      } else {
        setError(couldNotApprove('the development plan'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!currentUser) return;

    try {
      setCreating(true);
      setError('');

      const createDto: CreateIdpDto = {
        year: createYear,
        careerAspirations: undefined, // Optional on creation
        developmentGoals: [], // Optional on creation
      };

      const created = await idpService.createIdp(createDto);
      setCreateDialogOpen(false);
      
      // If we're in list view, refresh the list and navigate to the new IDP
      if (!yearParam) {
        const allIdps = await idpService.getMyIdps();
        setIdps(allIdps);
        navigate(`/individual-development-plan?year=${created.year}`);
      } else {
        // If we're in detail view, update the current IDP
        setIdp(created);
        const createdAspirations = created.careerAspirations || '';
        setCareerAspirations(createdAspirations);
        careerAspirationsRef.current = createdAspirations;
        setGoals([...created.developmentGoals].sort((a, b) => a.sequence - b.sequence));
        setEditing(true); // Automatically enter edit mode after creation
      }
      setError(''); // Clear any previous errors
    } catch (err: unknown) {
      console.error('Failed to create IDP:', err);
      if (err && typeof err === 'object' && 'response' in err) {
                setError(getApiErrorMessage(err, couldNotCreate('the development plan')));
      } else {
        setError(couldNotCreate('the development plan'));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleAddGoal = () => {
    setEditingGoal(null);
    setGoalForm({
      skillOrCompetency: '',
      resourcesAndActivities: '',
      targetDeadline: '',
      sequence: goals.length + 1,
    });
    setGoalDialogOpen(true);
  };

  const handleEditGoal = (goal: DevelopmentGoal) => {
    setEditingGoal(goal);
    setGoalForm({
      skillOrCompetency: goal.skillOrCompetency,
      resourcesAndActivities: goal.resourcesAndActivities,
      targetDeadline: formatCalendarDateIso(goal.targetDeadline),
      sequence: goal.sequence,
    });
    setGoalDialogOpen(true);
  };

  const handleDeleteGoalClick = (goal: DevelopmentGoal) => {
    setGoalToDelete(goal);
  };

  const handleConfirmDeleteGoal = () => {
    if (!goalToDelete) return;
    const newGoals = goals.filter((g) => g.id !== goalToDelete.id);
    setGoalToDelete(null);
    setGoals([...newGoals].sort((a, b) => a.sequence - b.sequence));
    // Deletion is batched: user must click Save to persist (same as add/edit)
  };

  const handleSaveGoal = () => {
    if (!goalForm.targetDeadline) {
      setError('Target deadline is required');
      return;
    }

    if (editingGoal) {
      // Update existing goal
      setGoals(
        goals.map((g) =>
          g.id === editingGoal.id
            ? {
                ...g,
                skillOrCompetency: goalForm.skillOrCompetency,
                resourcesAndActivities: goalForm.resourcesAndActivities,
                targetDeadline: goalForm.targetDeadline,
                sequence: goalForm.sequence,
              }
            : g
        )
      );
    } else {
      // Add new goal
      const newGoal: DevelopmentGoal = {
        id: `temp-${Date.now()}`,
        individualDevelopmentPlanId: idp!.id,
        skillOrCompetency: goalForm.skillOrCompetency,
        resourcesAndActivities: goalForm.resourcesAndActivities,
        targetDeadline: goalForm.targetDeadline,
        sequence: goalForm.sequence,
      };
      setGoals([...goals, newGoal].sort((a, b) => a.sequence - b.sequence));
    }
    setGoalDialogOpen(false);
  };

  const handleOpenQuarterlyReview = (slot: QuarterlyReviewSlot) => {
    setSelectedQuarterlyReview(slot);
    if (isExistingQuarterlyReview(slot)) {
      setQuarterlyReviewForm({
        managerComments: slot.managerComments || '',
        managerReviewDate: formatCalendarDateIso(slot.managerReviewDate),
        employeeComments: slot.employeeComments || '',
        employeeReviewDate: formatCalendarDateIso(slot.employeeReviewDate),
      });
    } else {
      setQuarterlyReviewForm({
        managerComments: '',
        managerReviewDate: '',
        employeeComments: '',
        employeeReviewDate: '',
      });
    }
    setQuarterlyReviewDialogOpen(true);
  };

  const handleSaveQuarterlyReview = async () => {
    if (!idp || !selectedQuarterlyReview) return;

    try {
      setSaving(true);
      setError('');

      const dto = {
        managerComments: quarterlyReviewForm.managerComments || undefined,
        employeeComments: quarterlyReviewForm.employeeComments || undefined,
      };

      const updated = await idpService.submitQuarterlyReview(idp.id, selectedQuarterlyReview.reviewCycleId, dto);

      // Refetch IDP so we have latest status (e.g. reverted to draft) and quarterly review data
      const isOwnIdp = idp.employeeId === currentUser?.id;
      const refetched = isOwnIdp
        ? await idpService.getMyIdp(idp.year)
        : await idpService.getEmployeeIdp(idp.employeeId, idp.year);
      if (refetched) {
        setIdp(refetched);
      } else {
        const updatedIdp = { ...idp };
        const reviewIndex = updatedIdp.quarterlyReviews.findIndex((r) => r.id === updated.id);
        if (reviewIndex >= 0) {
          updatedIdp.quarterlyReviews[reviewIndex] = updated;
        } else {
          updatedIdp.quarterlyReviews.push(updated);
        }
        setIdp(updatedIdp);
      }

      // Refresh audit trail to show the quarterly review submit and any revert event
      try {
        setLoadingAudit(true);
        const events = await idpService.getAuditTrail(idp.id);
        setAuditEvents(events);
      } catch (err: unknown) {
        console.error('Failed to refresh audit trail:', err);
      } finally {
        setLoadingAudit(false);
      }

      setQuarterlyReviewDialogOpen(false);
    } catch (err: unknown) {
      console.error('Failed to save quarterly review:', err);
      if (err && typeof err === 'object' && 'response' in err) {
                setError(getApiErrorMessage(err, couldNotSave('the quarterly review')));
      } else {
        setError(couldNotSave('the quarterly review'));
      }
    } finally {
      setSaving(false);
    }
  };

  const refreshIdpDocuments = async (idpId: string) => {
    const res = await apiService.get<{ id: string; fileName: string; contentType: string; uploadedByName: string; uploadedAt: string; fileSize: number; description?: string }[]>(
      `/individual-development-plans/${idpId}/supporting-documents`
    );
    setIdpDocuments(res.data || []);
  };

  const refreshIdpAuditTrail = async (idpId: string) => {
    try {
      setLoadingAudit(true);
      const events = await idpService.getAuditTrail(idpId);
      setAuditEvents(events);
    } catch (err: unknown) {
      console.error('Failed to refresh audit trail:', err);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleUploadIdpDocument = async (files: File[], description: string) => {
    if (!idp?.id) return;
    try {
      setUploadingIdpDoc(true);
      setError('');
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (description.trim()) formData.append('description', description.trim());
        await apiService.post(`/individual-development-plans/${idp.id}/supporting-documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIdpUploadDialogOpen(false);
      await refreshIdpDocuments(idp.id);
      await refreshIdpAuditTrail(idp.id);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.upload));
    } finally {
      setUploadingIdpDoc(false);
    }
  };

  const handleDownloadIdpDocument = async (documentId: string, fileName: string) => {
    if (!idp?.id) return;
    try {
      const response = await apiService.get(
        `/individual-development-plans/${idp.id}/supporting-documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.download));
    }
  };

  const handleViewIdpDocument = async (documentId: string, fileName: string, contentType: string) => {
    if (!idp?.id) return;
    try {
      setError('');
      const response = await apiService.get(
        `/individual-development-plans/${idp.id}/supporting-documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        setPreviewNotAvailableFileName(fileName);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.open));
    }
  };

  const handleDeleteIdpDocument = async (documentId: string, _fileName: string) => {
    if (!idp?.id) return;
    try {
      setDeletingIdpDocId(documentId);
      setError('');
      await apiService.delete(`/individual-development-plans/${idp.id}/supporting-documents/${documentId}`);
      setDeleteConfirmIdpDoc(null);
      await refreshIdpDocuments(idp.id);
      await refreshIdpAuditTrail(idp.id);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.delete));
    } finally {
      setDeletingIdpDocId(null);
    }
  };

  const handleUpdateIdpDocument = async (documentId: string, fileName: string, description: string) => {
    if (!idp?.id) return;
    try {
      setError('');
      await apiService.patch(`/individual-development-plans/${idp.id}/supporting-documents/${documentId}`, { fileName: fileName.trim(), description: description.trim() || undefined });
      setEditIdpDocDialogOpen(false);
      setEditingIdpDoc(null);
      await refreshIdpDocuments(idp.id);
      await refreshIdpAuditTrail(idp.id);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotUpdate('the document')));
    }
  };

  const isEmployee = () => {
    if (!idp || !currentUser) return false;
    return idp.employeeId === currentUser.id;
  };

  /** True only when current user is the manager of this IDP (not admin viewing own IDP). Used so employees never see/edit manager comments. */
  const canEditManagerCommentsInQuarterlyReview = () => {
    if (!idp || !currentUser) return false;
    return idp.managerId === currentUser.id;
  };

  /** True when user can add quarterly reviews or upload docs (employee or manager). Read-only admins see neither. */
  const canAddReviewOrUpload = () => isEmployee() || canEditManagerCommentsInQuarterlyReview();

  if (loading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  // Show table view when no year is specified and viewing own IDPs
  if (!yearParam && !employeeId) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {(teamParam === 'true' || pendingApprovalsParam === 'true') && (
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{ ml: -1 }}
                  title="Back"
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
              <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
                Individual Development Plans
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {filteredAndSortedIdps.length} IDP{filteredAndSortedIdps.length !== 1 ? 's' : ''}
              </Typography>
              {pendingApprovalsParam !== 'true' && teamParam !== 'true' && !urlDepartmentId && !urlDepartmentName && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Create IDP
                </Button>
              )}
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            {pendingApprovalsParam === 'true' 
              ? 'Review and approve Individual Development Plans submitted by your team members. Click on an IDP to view details and approve.'
              : teamParam === 'true'
              ? 'View all Individual Development Plans for your team members. Click on an IDP to view details.'
              : 'Create and manage your Individual Development Plans for each year. Track your career aspirations and development goals.'}
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Search IDPs"
                  placeholder="Search by year, status, or career aspirations..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      const value = e.target.value;
                      setStatusFilter(value);
                      setPage(0);
                      // Keep URL in sync when viewing team/pending-approvals (explicitly preserve context params)
                      if (teamParam === 'true' || pendingApprovalsParam === 'true') {
                        const params = new URLSearchParams();
                        // Pending approvals only contains With Manager IDPs - switching to Draft/Outstanding/Acknowledged
                        // would show 0 results. Use team view instead so we load full team IDPs.
                        const useTeamView =
                          pendingApprovalsParam === 'true' &&
                          value !== 'all' &&
                          value !== IdpStatus.WithManager.toString();
                        if (useTeamView) {
                          params.set('team', 'true');
                        } else {
                          if (pendingApprovalsParam === 'true') params.set('pending-approvals', 'true');
                          if (teamParam === 'true') params.set('team', 'true');
                        }
                        if (urlDepartmentId) params.set('departmentId', urlDepartmentId);
                        if (urlDepartmentName) params.set('departmentName', urlDepartmentName);
                        if (value !== 'all') {
                          const statusToUrl: Record<string, string> = {
                            outstanding: 'outstanding',
                            [IdpStatus.Draft.toString()]: 'draft',
                            [IdpStatus.WithManager.toString()]: 'withmanager',
                            [IdpStatus.Acknowledged.toString()]: 'acknowledged',
                            [IdpStatus.Completed.toString()]: 'completed',
                          };
                          params.set('status', statusToUrl[value] ?? value);
                        }
                        navigate(`/individual-development-plan?${params.toString()}`, { replace: true });
                      }
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="outstanding">Outstanding</MenuItem>
                    <MenuItem value={IdpStatus.Draft.toString()}>Draft</MenuItem>
                    <MenuItem value={IdpStatus.WithManager.toString()}>With manager</MenuItem>
                    <MenuItem value={IdpStatus.Acknowledged.toString()}>Acknowledged</MenuItem>
                    <MenuItem value={IdpStatus.Completed.toString()}>Completed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {(searchQuery.trim() !== '' || statusFilter !== 'all' || urlDepartmentId || urlDepartmentName) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  {(urlDepartmentId || urlDepartmentName) && (
                    <Chip
                      label={`Department: ${urlDepartmentName || urlDepartmentId}`}
                      onDelete={() => {
                        const params = new URLSearchParams(searchParams);
                        params.delete('departmentId');
                        params.delete('departmentName');
                        navigate(`/individual-development-plan?${params.toString()}`, { replace: true });
                        setPage(0);
                      }}
                      size="small"
                      color="warning"
                    />
                  )}
                  {searchQuery.trim() !== '' && (
                    <Chip
                      label={`Search: "${searchQuery}"`}
                      onDelete={() => {
                        setSearchQuery('');
                        setPage(0);
                      }}
                      size="small"
                      color="primary"
                    />
                  )}
                  {statusFilter !== 'all' && (
                    <Chip
                      label={`Status: ${statusFilter === 'outstanding' ? 'Outstanding' : formatIdpStatus(parseInt(statusFilter, 10) as IdpStatus)}`}
                      onDelete={() => {
                        setStatusFilter('all');
                        setPage(0);
                        if (teamParam === 'true' || pendingApprovalsParam === 'true') {
                          const params = new URLSearchParams();
                          if (pendingApprovalsParam === 'true') params.set('pending-approvals', 'true');
                          if (teamParam === 'true') params.set('team', 'true');
                          if (urlDepartmentId) params.set('departmentId', urlDepartmentId);
                          if (urlDepartmentName) params.set('departmentName', urlDepartmentName);
                          navigate(`/individual-development-plan?${params.toString()}`, { replace: true });
                        }
                      }}
                      size="small"
                      color="info"
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {loading && idps.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAndSortedIdps.length === 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {(() => {
                      const hasActiveFilters = searchQuery.trim() || statusFilter !== 'all';
                      return hasActiveFilters ? 'No IDPs match your search or filters.' : 'No Development Plans';
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const hasActiveFilters = searchQuery.trim() || statusFilter !== 'all';
                      if (hasActiveFilters) {
                        return 'Try adjusting your search or filter criteria.';
                      }
                      return 'Use the "Create IDP" button above to create your first Individual Development Plan.';
                    })()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Paper variant="outlined" sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table key={displayTimeZoneId}>
                  <TableHead>
                    <TableRow>
                      {(pendingApprovalsParam === 'true' || teamParam === 'true' || urlDepartmentId || urlDepartmentName) && (
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          Employee
                        </TableCell>
                      )}
                      <TableCell>
                        <SortableTableHeader
                          label={useCalendarYear ? 'Year' : 'Fiscal Year'}
                          active={sortBy === 'year'}
                          direction={sortBy === 'year' ? sortDirection : 'asc'}
                          onClick={() => handleSort('year')}
                        />
                      </TableCell>
                      <TableCell>
                        <SortableTableHeader
                          label="Status"
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortDirection : 'asc'}
                          onClick={() => handleSort('status')}
                        />
                      </TableCell>
                      <TableCell>
                        <SortableTableHeader
                          label="Created"
                          active={sortBy === 'createdAt'}
                          direction={sortBy === 'createdAt' ? sortDirection : 'asc'}
                          onClick={() => handleSort('createdAt')}
                        />
                      </TableCell>
                      <TableCell><strong>Goals</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedIdps.map((idpItem) => {
                      const status = idpItem.status;
                      const isSubmitted = status === IdpStatus.WithManager || status === 2 || status === 'Submitted';
                      
                      return (
                        <TableRow key={idpItem.id} hover>
                          {(pendingApprovalsParam === 'true' || teamParam === 'true' || urlDepartmentId || urlDepartmentName) && (
                            <TableCell>
                              <Typography variant="body2" fontWeight="500">
                                {idpItem.employeeName || 'Unknown'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>{idpItem.year}</TableCell>
                          <TableCell>
                            <Chip
                              label={formatIdpStatus(idpItem.status)}
                              color={getIdpStatusColor(idpItem.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(idpItem.createdAt, { includeTime: true })}</TableCell>
                          <TableCell>{idpItem.developmentGoals.length}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const preserveParams = new URLSearchParams(searchParams);
                                  if (pendingApprovalsParam === 'true') {
                                    preserveParams.set('pending-approvals', 'true');
                                  }
                                  if (teamParam === 'true') {
                                    preserveParams.set('team', 'true');
                                  }
                                  if (urlDepartmentId) preserveParams.set('departmentId', urlDepartmentId);
                                  if (urlDepartmentName) preserveParams.set('departmentName', urlDepartmentName);
                                  if (urlStatusParam) preserveParams.set('status', urlStatusParam);
                                  const qs = preserveParams.toString();
                                  if (pendingApprovalsParam === 'true' || teamParam === 'true') {
                                    navigate(`/individual-development-plan/${idpItem.employeeId}/${idpItem.year}${qs ? `?${qs}` : ''}`);
                                  } else {
                                    navigate(`/individual-development-plan?year=${idpItem.year}`);
                                  }
                                }}
                                color="primary"
                                title="View IDP"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              {(() => {
                                // Check if user can edit this IDP (only for own IDPs, not pending approvals)
                                if (!currentUser || pendingApprovalsParam === 'true') return null;
                                const isDraft = status === IdpStatus.Draft || status === 1 || status === 'Draft';
                                const isActive = status === IdpStatus.Acknowledged || status === 3 || status === 'Active' || status === 'Acknowledged';
                                const canEditIdp = idpItem.employeeId === currentUser.id && (isDraft || isSubmitted || isActive);
                                
                                if (!canEditIdp) return null;
                                
                                return (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/individual-development-plan?year=${idpItem.year}&edit=true`);
                                    }}
                                    title="Edit IDP"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                );
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredAndSortedIdps.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
              />
            </Paper>
          )}

          <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Create Individual Development Plan</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={createYear}
                  onChange={(e) => setCreateYear(parseInt(e.target.value, 10))}
                  inputProps={{ min: 2000, max: 2100 }}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  You can add career aspirations and development goals after creating the IDP.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </AppLayout>
    );
  }

  // Show detail view when year is specified or viewing employee's IDP
  if (!idp && yearParam) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <IconButton 
              onClick={() => {
                // Use browser history to go back - this is more reliable than hardcoding URLs
                // If there's no history, fall back to appropriate default page
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  // Fallback: navigate based on context (preserve department filter)
                  const params = new URLSearchParams();
                  if (pendingApprovalsParam === 'true') {
                    params.set('pending-approvals', 'true');
                  } else if (teamParam === 'true' || (employeeId && employeeId !== currentUser?.id)) {
                    params.set('team', 'true');
                  }
                  if (urlDepartmentId) params.set('departmentId', urlDepartmentId);
                  if (urlDepartmentName) params.set('departmentName', urlDepartmentName);
                  const qs = params.toString();
                  navigate(`/individual-development-plan${qs ? `?${qs}` : ''}`);
                }
              }} 
              sx={{ ml: -1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight="600">
              Individual Development Plan
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            IDP not found for this year. Use the navigation to create one when ready.
          </Alert>

          <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Create Individual Development Plan</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={createYear}
                  onChange={(e) => setCreateYear(parseInt(e.target.value, 10))}
                  inputProps={{ min: 2000, max: 2100 }}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  You can add career aspirations and development goals after creating the IDP.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </AppLayout>
    );
  }

  if (!idp) {
    return (
      <AppLayout>
        <Alert severity="info">IDP not found.</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box
        className="idp-detail-print"
        sx={{ maxWidth: '1400px', mx: 'auto', pb: 4, '@media print': { maxWidth: '100%' } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton 
              onClick={() => {
                // Use browser history to go back - this is more reliable than hardcoding URLs
                // If there's no history, fall back to appropriate default page
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  // Fallback: navigate based on context (preserve department filter)
                  const params = new URLSearchParams();
                  if (pendingApprovalsParam === 'true') {
                    params.set('pending-approvals', 'true');
                  } else if (teamParam === 'true' || (employeeId && employeeId !== currentUser?.id)) {
                    params.set('team', 'true');
                  }
                  if (urlDepartmentId) params.set('departmentId', urlDepartmentId);
                  if (urlDepartmentName) params.set('departmentName', urlDepartmentName);
                  const qs = params.toString();
                  navigate(`/individual-development-plan${qs ? `?${qs}` : ''}`);
                }
              }} 
              sx={{ ml: -1, ...printHiddenSx }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight="600">
              Individual Development Plan
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip label={formatIdpStatus(idp.status)} color={getIdpStatusColor(idp.status)} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ...printHiddenSx }}>
            {canEdit() && !editing && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {canEdit() && editing && (
              <>
                <Tooltip title="Exit edit mode without saving. Any unsaved changes will be discarded.">
                  <Button variant="outlined" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </Tooltip>
                {hasUnsavedChanges && (
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !careerAspirations?.trim()}
                  >
                    Save
                  </Button>
                )}
              </>
            )}
            {canSubmit() && (
              <Tooltip
                title={
                  hasUnsavedChanges
                    ? 'Save your changes first, then you can submit.'
                    : goals.length === 0
                      ? 'Add at least one development goal to submit your plan.'
                      : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={saving || !careerAspirations?.trim() || goals.length === 0 || hasUnsavedChanges}
                  >
                    Submit
                  </Button>
                </span>
              </Tooltip>
            )}
            {canApprove() && (
              <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleApprove} disabled={saving}>
                Approve
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ textTransform: 'none' }}
            >
              Print
            </Button>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {editing && canEdit() && hasUnsavedChanges && (
          <Alert severity="info" sx={{ mb: 2, ...printHiddenSx }} icon={false}>
            <Typography variant="body2" fontWeight="600">
              Your changes are not saved yet.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Click <strong>Save</strong> (in the bar at the bottom of the page, or next to Cancel above) to save before leaving or submitting.
            </Typography>
          </Alert>
        )}

        {/* Employee Information */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={2} mt={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Employee:
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {idp.employeeName}
                  </Typography>
                </Box>
              </Grid>
              {idp.managerName && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Manager:
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      {idp.managerName}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarTodayIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {useCalendarYear ? 'Year:' : 'Fiscal Year:'}
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {idp.year}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Career Aspirations */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Summary of Employee Career Aspirations
            </Typography>
            {editing && canEdit() ? (
              <CareerAspirationsField
                value={careerAspirations}
                onChange={setCareerAspirations}
                latestValueRef={careerAspirationsRef}
              />
            ) : (
              <Paper sx={{ p: 2, bgcolor: '#f5f7fa', mt: 2, minHeight: 100 }}>
                <Typography variant="body2" color="text.secondary">
                  {idp.careerAspirations}
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>

        {/* Development Goals */}
        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight="600">
                Development Plan in Action
              </Typography>
              {editing && canEdit() && (
                <Box sx={printHiddenSx}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddGoal}>
                  Add Goal
                </Button>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {goals.length === 0
                ? 'At least one development goal is required before you can submit this IDP for approval.'
                : 'Add more goals with the button above if needed, or submit when ready.'}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Skills/Competencies to Develop</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Resources and Activities</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Target/Deadline</TableCell>
                    {editing && canEdit() && (
                      <TableCell sx={[{ fontWeight: 'bold' }, printHiddenSx]} align="right">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {goals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={editing && canEdit() ? 4 : 3} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No development goals added yet.
                        </Typography>
                        {canEdit() && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Add at least one goal before you can submit your plan.
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    goals.map((goal) => (
                      <TableRow key={goal.id}>
                        <TableCell>{goal.skillOrCompetency}</TableCell>
                        <TableCell>{goal.resourcesAndActivities}</TableCell>
                        <TableCell>{formatDate(goal.targetDeadline)}</TableCell>
                        {editing && canEdit() && (
                          <TableCell align="right" sx={[{ whiteSpace: 'nowrap' }, printHiddenSx]}>
                            <IconButton size="small" onClick={() => handleEditGoal(goal)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteGoalClick(goal)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Quarterly Reviews - hidden when SHOW_QUARTERLY_REVIEW_SECTION is false (not in use, was causing confusion) */}
        {SHOW_QUARTERLY_REVIEW_SECTION && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Quarterly Review of Individual&apos;s Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {canAddReviewOrUpload()
                  ? 'Click a quarter card below to add or view your review for that period. Add your comments and date, then Save.'
                  : 'Quarterly review progress for this plan. Only the employee or their manager can add reviews.'}
              </Typography>
              <Grid container spacing={2} mt={1}>
                {[1, 2, 3, 4].map((reviewNumber) => {
                  const review = idp.quarterlyReviews.find((r) => r.reviewNumber === reviewNumber);
                  const cycleForQuarter = cyclesByQuarter[reviewNumber];
                  const slot: QuarterlyReviewSlot | null = review
                    ? review
                    : cycleForQuarter
                      ? { reviewNumber, reviewCycleId: cycleForQuarter.id, reviewCycleName: cycleForQuarter.name }
                      : null;
                  const canAdd = canAddReviewOrUpload();
                  const hasContent = review && (review.managerComments || review.employeeComments || review.managerReviewDate || review.employeeReviewDate);
                  const isClickable = !!slot && (canAdd || !!hasContent);
                  const showLink = isClickable && canAdd && !review?.isCompleted;
                  const linkLabel = review && hasContent ? 'View / Edit your review →' : 'Add your review →';
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={reviewNumber}>
                      <Paper
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: review?.isCompleted ? 'success.main' : 'divider',
                          bgcolor: review?.isCompleted ? 'success.light' : 'background.paper',
                          cursor: isClickable ? 'pointer' : 'default',
                          '&:hover': isClickable ? { bgcolor: 'action.hover' } : undefined,
                        }}
                        onClick={() => isClickable && slot && handleOpenQuarterlyReview(slot)}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2" fontWeight="600">
                            {reviewNumber === 1 ? '1st' : reviewNumber === 2 ? '2nd' : reviewNumber === 3 ? '3rd' : '4th'} Review
                          </Typography>
                          {review?.isCompleted && <CheckCircleIcon color="success" fontSize="small" />}
                        </Box>
                        {review ? (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              {review.reviewCycleName}
                            </Typography>
                            <Box mt={1}>
                              {review.managerReviewDate && (
                                <Typography variant="caption" display="block">
                                  Manager: {formatDateStandard(review.managerReviewDate)}
                                </Typography>
                              )}
                              {review.employeeReviewDate && (
                                <Typography variant="caption" display="block">
                                  Employee: {formatDateStandard(review.employeeReviewDate)}
                                </Typography>
                              )}
                            </Box>
                            {showLink && (
                              <Typography variant="body2" color="primary.main" fontWeight={600} sx={{ mt: 1 }}>
                                {linkLabel}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color={showLink ? 'primary.main' : 'text.secondary'} fontWeight={showLink ? 600 : 400}>
                            {showLink ? linkLabel : (slot ? 'Not started' : 'Not yet available')}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Supporting Documents — omitted from print (attachments out of scope; same as review detail). */}
        <Card
          sx={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            mb: 3,
            '@media print': { display: 'none !important' },
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                Supporting Documents
              </Typography>
              {canAddReviewOrUpload() && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon />}
                  onClick={() => setIdpUploadDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Upload Document
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add evidence, certificates, or notes at any time. This plan is a working agreement and can be updated as you progress.
            </Typography>
            {loadingIdpDocs ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : idpDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No documents yet. Use &quot;Upload Document&quot; to add supporting files.
              </Typography>
            ) : (
              <SupportingDocumentsTable
                documents={idpDocuments.map((d) => ({
                  id: d.id,
                  fileName: d.fileName,
                  description: d.description,
                  uploadedByName: d.uploadedByName,
                  uploadedAt: d.uploadedAt,
                  fileSize: d.fileSize,
                  contentType: d.contentType,
                }))}
                onView={handleViewIdpDocument}
                onDownload={handleDownloadIdpDocument}
                onDelete={(documentId, fileName) => setDeleteConfirmIdpDoc({ documentId, fileName })}
                canEdit={canAddReviewOrUpload()}
                onEdit={(documentId, fileName, description) => {
                  setEditingIdpDoc({ id: documentId, fileName, description });
                  setEditIdpDocDialogOpen(true);
                }}
                formatDate={(d) => formatDate(d, { includeTime: true })}
              />
            )}
          </CardContent>
        </Card>

        {/* Audit Trail */}
        {idp && (
          <Accordion sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" fontWeight="600">
                Audit Trail
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loadingAudit ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : auditEvents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No audit events found.
                </Typography>
              ) : (
                <Box>
                  {auditEvents.map((event) => (
                    <Box key={event.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">
                          {getIdpEventTypeLabel(event.eventType)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(event.eventAt, { includeTime: true })}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        By: {event.actorName}
                      </Typography>
                      {event.details && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {event.details}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Sticky Save bar only when there are unsaved changes (long page: Save without scrolling up) */}
        {editing && canEdit() && idp && hasUnsavedChanges && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 4,
              py: 2,
              px: 2,
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              '@media print': { display: 'none !important' },
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight="500">
              Unsaved changes — click Save to save your changes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => setEditing(false)} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !careerAspirations?.trim()}
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}

        {/* Goal Dialog */}
        <Dialog open={goalDialogOpen} onClose={() => setGoalDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingGoal ? 'Edit Development Goal' : 'Add Development Goal'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Skills/Competencies to Develop"
                value={goalForm.skillOrCompetency}
                onChange={(e) => setGoalForm({ ...goalForm, skillOrCompetency: e.target.value })}
                fullWidth
                required
                multiline
                rows={2}
              />
              <TextField
                label="Resources and Activities"
                value={goalForm.resourcesAndActivities}
                onChange={(e) => setGoalForm({ ...goalForm, resourcesAndActivities: e.target.value })}
                fullWidth
                required
                multiline
                rows={3}
                placeholder="e.g., training, mentor, online courses"
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppDatePicker
                  label="Target/Deadline"
                  value={goalForm.targetDeadline ? new Date(goalForm.targetDeadline) : null}
                  onChange={(date) =>
                    setGoalForm({ ...goalForm, targetDeadline: date ? formatCalendarDateIso(date) : '' })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                  shouldDisableDate={shouldDisableDate}
                />
              </LocalizationProvider>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoalDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveGoal}
              disabled={!goalForm.skillOrCompetency || !goalForm.resourcesAndActivities || !goalForm.targetDeadline}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete development goal confirmation */}
        <Dialog open={!!goalToDelete} onClose={() => setGoalToDelete(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Remove development goal from list?</DialogTitle>
          <DialogContent>
            {goalToDelete && (
              <Typography variant="body2" color="text.secondary">
                {(goalToDelete.skillOrCompetency?.trim() || 'This goal').length > 80
                  ? (goalToDelete.skillOrCompetency?.trim() || 'This goal').slice(0, 80) + '…'
                  : goalToDelete.skillOrCompetency?.trim() || 'This goal'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              The goal will be removed from your list. Click <strong>Save</strong> (at the top or bottom of the page) to save this and any other changes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoalToDelete(null)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDeleteGoal} sx={{ textTransform: 'none' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quarterly Review Dialog - only rendered when quarterly review section is enabled */}
        {SHOW_QUARTERLY_REVIEW_SECTION && (
        <Dialog open={quarterlyReviewDialogOpen} onClose={() => setQuarterlyReviewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Quarterly Review - {selectedQuarterlyReview?.reviewNumber === 1
              ? '1st'
              : selectedQuarterlyReview?.reviewNumber === 2
              ? '2nd'
              : selectedQuarterlyReview?.reviewNumber === 3
              ? '3rd'
              : '4th'}{' '}
            Review
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {selectedQuarterlyReview && (
                <Typography variant="body2" color="text.secondary">
                  Review Cycle: {selectedQuarterlyReview.reviewCycleName}
                </Typography>
              )}
              {/* Read-only view for admin (not employee, not manager): show both comments */}
              {!canAddReviewOrUpload() && (quarterlyReviewForm.managerComments || quarterlyReviewForm.employeeComments || quarterlyReviewForm.managerReviewDate || quarterlyReviewForm.employeeReviewDate) && (
                <>
                  {(quarterlyReviewForm.managerComments || quarterlyReviewForm.managerReviewDate) && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                        Manager&apos;s Comments
                      </Typography>
                      {quarterlyReviewForm.managerComments && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{quarterlyReviewForm.managerComments}</Typography>
                      )}
                      {quarterlyReviewForm.managerReviewDate && (
                        <Typography variant="caption" color="text.secondary">
                          Date: {formatDateStandard(quarterlyReviewForm.managerReviewDate)}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {(quarterlyReviewForm.employeeComments || quarterlyReviewForm.employeeReviewDate) && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                        Employee&apos;s Comments
                      </Typography>
                      {quarterlyReviewForm.employeeComments && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{quarterlyReviewForm.employeeComments}</Typography>
                      )}
                      {quarterlyReviewForm.employeeReviewDate && (
                        <Typography variant="caption" color="text.secondary">
                          Date: {formatDateStandard(quarterlyReviewForm.employeeReviewDate)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
              {/* Read-only other party's comments when viewing as manager */}
              {canEditManagerCommentsInQuarterlyReview() && (quarterlyReviewForm.employeeComments || quarterlyReviewForm.employeeReviewDate) && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                    Employee&apos;s Comments
                  </Typography>
                  {quarterlyReviewForm.employeeComments && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{quarterlyReviewForm.employeeComments}</Typography>
                  )}
                  {quarterlyReviewForm.employeeReviewDate && (
                    <Typography variant="caption" color="text.secondary">
                      Date: {formatDateStandard(quarterlyReviewForm.employeeReviewDate)}
                    </Typography>
                  )}
                </Box>
              )}
              {/* Read-only other party's comments when viewing as employee */}
              {isEmployee() && !canEditManagerCommentsInQuarterlyReview() && (quarterlyReviewForm.managerComments || quarterlyReviewForm.managerReviewDate) && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom color="text.secondary">
                    Manager&apos;s Comments
                  </Typography>
                  {quarterlyReviewForm.managerComments && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{quarterlyReviewForm.managerComments}</Typography>
                  )}
                  {quarterlyReviewForm.managerReviewDate && (
                    <Typography variant="caption" color="text.secondary">
                      Date: {formatDateStandard(quarterlyReviewForm.managerReviewDate)}
                    </Typography>
                  )}
                </Box>
              )}
              {canEditManagerCommentsInQuarterlyReview() && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Manager's Comments
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={quarterlyReviewForm.managerComments}
                    onChange={(e) => setQuarterlyReviewForm({ ...quarterlyReviewForm, managerComments: e.target.value })}
                    placeholder="Add your comments..."
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
              {isEmployee() && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Employee's Comments
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={quarterlyReviewForm.employeeComments}
                    onChange={(e) => setQuarterlyReviewForm({ ...quarterlyReviewForm, employeeComments: e.target.value })}
                    placeholder="Add your comments..."
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
              {selectedQuarterlyReview && isExistingQuarterlyReview(selectedQuarterlyReview) && selectedQuarterlyReview.isCompleted && (
                <Alert severity="success">This quarterly review has been completed by both manager and employee.</Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQuarterlyReviewDialogOpen(false)}>{canAddReviewOrUpload() ? 'Cancel' : 'Close'}</Button>
            {canAddReviewOrUpload() && (
              <Button variant="contained" onClick={handleSaveQuarterlyReview} disabled={saving}>
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
        )}

        {/* Upload IDP Supporting Document Dialog */}
        <DocumentUploadDialog
          open={idpUploadDialogOpen}
          onClose={() => setIdpUploadDialogOpen(false)}
          onUpload={handleUploadIdpDocument}
          uploading={uploadingIdpDoc}
        />

        <Dialog
          open={editIdpDocDialogOpen}
          onClose={() => { setEditIdpDocDialogOpen(false); setEditingIdpDoc(null); setError(''); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Document</DialogTitle>
          <DialogContent>
            {editingIdpDoc && (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="File name"
                  value={editingIdpDoc.fileName}
                  onChange={(e) => setEditingIdpDoc({ ...editingIdpDoc, fileName: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={editingIdpDoc.description}
                  onChange={(e) => setEditingIdpDoc({ ...editingIdpDoc, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
                <DialogActions sx={{ mt: 2, px: 0 }}>
                  <Button onClick={() => { setEditIdpDocDialogOpen(false); setEditingIdpDoc(null); setError(''); }} sx={{ textTransform: 'none' }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => editingIdpDoc && handleUpdateIdpDocument(editingIdpDoc.id, editingIdpDoc.fileName, editingIdpDoc.description)}
                    disabled={!editingIdpDoc?.fileName.trim()}
                    sx={{ textTransform: 'none' }}
                  >
                    Save
                  </Button>
                </DialogActions>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!deleteConfirmIdpDoc}
          onClose={() => setDeleteConfirmIdpDoc(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Document</DialogTitle>
          <DialogContent>
            {deleteConfirmIdpDoc && (
              <Typography variant="body1">
                Delete &quot;{deleteConfirmIdpDoc.fileName}&quot;? This cannot be undone.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteConfirmIdpDoc(null)} disabled={!!deletingIdpDocId} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!!deletingIdpDocId}
              onClick={() => deleteConfirmIdpDoc && handleDeleteIdpDocument(deleteConfirmIdpDoc.documentId, deleteConfirmIdpDoc.fileName)}
              sx={{ textTransform: 'none' }}
            >
              {deletingIdpDocId ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!previewNotAvailableFileName} onClose={() => setPreviewNotAvailableFileName(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Preview not available</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This file type cannot be displayed in the browser. Please use Download to open it on your device.
            </Typography>
            {previewNotAvailableFileName && (
              <Typography variant="body2" sx={{ mt: 1 }} fontWeight={500}>
                File: {previewNotAvailableFileName}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewNotAvailableFileName(null)} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
        <Box sx={printHiddenSx}>
          {showLegacyHelp && (
            <IdpHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
          )}
        </Box>
    </AppLayout>
  );
}
